import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClubData, NonPartnerClubData, continentCountries } from "../../types";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { VFC, APIResponse, DomExceptionName } from "types";
import Loader from "components/Loader";
import * as R from "ramda";
import { Form, Field } from "react-final-form";
import Select, { SingleValue } from "react-select";
import NonPartnerClubCard from "./nonPartnerClubCard";
import Modal from "ui-kit/src/Modal";
import Migrate from "../migrate/Migrate";
import ClubModal from "./ClubModal";
import TristateButton from "components/TristateButton/TristateButton";
import { _ } from "i18n";
import client from "libs/client";
import { useToast } from "ui-kit";

interface NonPartnerClubManagementProps {
	activeMenu: string;
	onPlayerCountClick: (club: any) => void;
	countries: { [key: string]: string };
	countriesWithContinents: continentCountries[];
}

const NonPartnerClubManagement: VFC<NonPartnerClubManagementProps> = ({ onPlayerCountClick, activeMenu, countries, countriesWithContinents }) => {
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showClubModal, setShowClubModal] = useState(false);
	const [hasMoreData, setHasMoreData] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const showLoaderRef = useRef(showLoader);
	const isSubmitted = useRef(false);

	useEffect(() => {
		showLoaderRef.current = showLoader;
	}, [showLoader]);

	const [countrieslist, setCountriesList] = useState<{ value: string; label: string }[]>([]);

	const [selectedClub, setSelectedClub] = useState<NonPartnerClubData | null>(null);
	const [selectedMigrationType, setSelectedMigrationType] = useState<string>("");
	const [geographicalAreaList, setGeographicalAreaList] = useState<{ value: string; label: string }[]>([]);

	const [clubData, setClubData] = useState<NonPartnerClubData[]>([]);

	const [showMigrate, setShowMigrate] = useState(false);

	const { addSuccessToast, addErrorToast } = useToast();

	const start = useRef(0);
	const totalRecords = useRef(0);
	const search = useRef("");

	const [defaultCountry, setDefaultCountry] = useState<{ value: string; label: string } | null>(null);
	const isDefaultCountryEmpty = useRef(true);

	const filters = useRef<any>({
		country: null,
	});

	const clubListFetchEnabledRef = useRef(false);

	const onDeleteActionClick = (club: any) => {
		setSelectedClub(club);
		setShowDeleteModal(true);
	};

	const onEditActionClick = (club: any) => {
		setSelectedClub(club);
		setShowClubModal(true);
	};

	const onMigrateActionClick = (club: any, type: string) => {
		setSelectedClub(club);
		setSelectedMigrationType(type);
		setShowMigrate(true);
	};

	const onDelete = async () => {
		if (!selectedClub) return;

		const response = await client.deleteNPClub.mutation({ clubId: selectedClub?.id });

		if (response?.data.status === APIResponse.SUCCESS) {
			if (response?.data.message) {
				addSuccessToast(response?.data.message);
			}
			start.current = 0;
			fetchData();
		} else {
			if (response?.data.message) {
				addErrorToast(response?.data.message);
			}
		}
	};

	const onUpdateClub = async (values: NonPartnerClubData) => {
		isSubmitted.current = true;
		try {
			const response = await client.addUpdateNPClub.mutation({ body: { nonPartnerClub: values } });

			if (response?.data.status === APIResponse.SUCCESS) {
				if (response?.data.message) {
					addSuccessToast(response?.data.message);
				}
				if (values.id) {
					setClubData((prevData: NonPartnerClubData[]) => {
						return prevData.map((data) => {
							if (data.id === values.id) {
								return { ...values, playersCount: data.playersCount, gamesCount: data.gamesCount };
							}
							return data;
						});
					});
				} else {
					clubListFetchEnabledRef.current = true;
					start.current = 0;
					fetchData();
				}
				setShowClubModal(false);
			} else {
				if (response?.data.message) {
					addErrorToast(response?.data.message);
				}
			}
		} catch {
			// A thrown mutation (network error / 5xx) must not leave the modal frozen with
			// the Save button stuck disabled. Surface the error and let `finally` re-enable it.
			addErrorToast(_("eyeball.something.wrong"));
		} finally {
			isSubmitted.current = false;
		}
	};

	const onChangeCountry = (e: SingleValue<{ value: string; label: string }>) => {
		if (e?.value) {
			updateFilter(e.value, "country");
			setDefaultCountry(e);
		} else {
			updateFilter("", "country");
			setDefaultCountry(null);
		}
	};

	const getNonPartnerCountriesData = useCallback(async (signal?: AbortSignal) => {
		try {
			const res = await client.getCountries.query(signal);
			if (signal?.aborted) {
				return;
			}
			if (res) {
				const list = res.data as { value: string; label: string }[];
				if (list.length > 0) {
					setCountriesList(list);
					return list;
				}
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
				return;
			}
			throw e;
		}
	}, []);

	const getGeographicalAreas = useCallback(async (signal?: AbortSignal) => {
		try {
			const response = await client.getGeographicalAreaList.query(signal);
			if (signal?.aborted) {
				return;
			}
			if (response?.status === 200 && response.data) {
				setGeographicalAreaList(response.data as { value: string; label: string }[]);
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
				return;
			}
			throw e;
		}
	}, []);

	const isFilterActive = (v: unknown) => v !== null && v !== undefined && v !== "";

	const hasClubListQueryCriteria = () => {
		const searchTrimmed = (search.current ?? "").trim();
		return (
			isFilterActive(filters.current.country) ||
			isFilterActive(filters.current.showcase) ||
			searchTrimmed.length > 0
		);
	};

	const fetchData = async (loadMore: boolean = false, signal?: AbortSignal) => {
		if (!clubListFetchEnabledRef.current) {
			return;
		}

		if (!hasClubListQueryCriteria()) {
			clubListFetchEnabledRef.current = false;
			setShowLoader(false);
			setHasMoreData(false);
			if (!loadMore) {
				setClubData([]);
			}
			return;
		}

		if (isDefaultCountryEmpty.current) {
			await getNonPartnerCountriesData(signal);
			if (signal?.aborted) {
				return;
			}
			start.current = 0;
			isDefaultCountryEmpty.current = false;
		}

		const pageStart = start.current;
		const param = {
			start: start.current,
			length: 10,
			search: search.current,
			...(filters.current.country ? { country: filters.current.country } : {}),
			showcase: filters.current.showcase ? parseInt(filters.current.showcase as string) : -1,
		};

		try {
			const res = await client.getNPClubs.query(param, signal);
			if (signal?.aborted) {
				return;
			}
			if (res) {
				const response = res.data;
				if (response.status === APIResponse.SUCCESS) {
					totalRecords.current = response.recordsFiltered ?? 0;
					setShowLoader(false);
					const batch = (response.data ?? []) as NonPartnerClubData[];
					setClubData((prevData) => {
						if (!loadMore) {
							return batch;
						}
						if (R.equals(prevData, batch) === false) {
							return [...new Set([...prevData, ...batch])];
						}
						return prevData;
					});
					start.current = pageStart + batch.length;
					const recordsFiltered = response.recordsFiltered ?? 0;
					setHasMoreData(pageStart + batch.length < recordsFiltered);
				} else {
					setShowLoader(false);
					setHasMoreData(false);
				}
			} else {
				setShowLoader(false);
				setHasMoreData(false);
			}
		} catch (e: unknown) {
			if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
				return;
			}
			setShowLoader(false);
			setHasMoreData(false);
		}
	};

	const fetchDataRef = useRef(fetchData);
	fetchDataRef.current = fetchData;

	const requestFetchWithLoaderGate = () => {
		start.current = 0;
		setHasMoreData(false);
		setClubData([]);
		if (!hasClubListQueryCriteria()) {
			clubListFetchEnabledRef.current = false;
			setShowLoader(false);
			return;
		}
		clubListFetchEnabledRef.current = true;
		if (showLoaderRef.current) {
			fetchData();
		} else {
			setShowLoader(true);
		}
	};

	const updateFilter = (value: string | number | undefined, id: string) => {
		const hadActiveFilter = isFilterActive(filters.current[id]);
		const next = value !== undefined && value !== null && value !== "" ? value : null;
		if (next !== null) {
			filters.current[id] = next;
		} else {
			filters.current[id] = null;
		}
		const hasActiveFilter = isFilterActive(filters.current[id]);

		const shouldFetchClubs = hasActiveFilter || (hadActiveFilter && !hasActiveFilter);
		if (!shouldFetchClubs) {
			return;
		}

		requestFetchWithLoaderGate();
	};

	const onSearchSubmit = (values: { search?: string }) => {
		search.current = (values.search ?? "").trim();
		if (!hasClubListQueryCriteria()) {
			clubListFetchEnabledRef.current = false;
			setHasMoreData(false);
			setClubData([]);
			setShowLoader(false);
			return;
		}
		requestFetchWithLoaderGate();
	};

	useEffect(() => {
		if (!showLoader) {
			return;
		}
		const controller = new AbortController();
		void fetchDataRef.current(false, controller.signal);
		return () => {
			controller.abort();
		};
	}, [showLoader]);

	const containerRef = useRef(null);
	const intersectionObserverOptions = useMemo(
		() => ({ root: null, rootMargin: "0px", threshold: 1.0 }),
		[],
	);

	useEffect(() => {
		if (activeMenu !== "non_partner_club") {
			return;
		}
		window.scrollTo({ top: 0, left: 0 });
		const controller = new AbortController();
		const { signal } = controller;
		void (async () => {
			try {
				await Promise.all([getNonPartnerCountriesData(signal), getGeographicalAreas(signal)]);
			} catch (e: unknown) {
				if (e instanceof DOMException && e.name === DomExceptionName.AbortError) {
					return;
				}
				const message = e instanceof Error ? e.message : undefined;
				if (message) {
					addErrorToast(message);
				}
			}
		})();
		return () => {
			controller.abort();
		};
	}, [activeMenu, addErrorToast, getNonPartnerCountriesData, getGeographicalAreas]);

	useEffect(() => {
		if (showMigrate) {
			return;
		}

		const element = containerRef.current;
		const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
			const [entry] = entries;
			if (!entry.isIntersecting || !clubListFetchEnabledRef.current) {
				return;
			}
			if (start.current < totalRecords.current) {
				void fetchDataRef.current(true);
			} else {
				setHasMoreData(false);
			}
		}, intersectionObserverOptions);

		if (element) {
			observer.observe(element);
		}

		return () => {
			if (element) {
				observer.unobserve(element);
			}
		};
	}, [intersectionObserverOptions, showMigrate]);

	return (
		<>
			{!showMigrate && (
				<div className="nonpartner-clubs-container">
					<div className="product-management-heading-container">
						<div className="product-management-heading"> {_("resource.non_partner_clubs")} </div>
						<div className="top-form-container">
							<button
								className="btn btn-light-blue"
								onClick={() => {
									setShowClubModal(true);
									setSelectedClub(null);
								}}
							>
								{_("filter.add_club")}
							</button>
						</div>
					</div>
					<div className="form-filter-container">
						<div className="filter-field">
							<label>{_("videos.table.th.country")}</label>
							<Select className="react-select" isClearable options={countrieslist} onChange={onChangeCountry} value={defaultCountry} />
						</div>
						<div className="filter-field">
							<label> {_("label.search")}</label>
							<Form
								onSubmit={onSearchSubmit}
								render={({ handleSubmit, submitting }) => (
									<>
										<form onSubmit={handleSubmit}>
											<Field name="search" type="text">
												{({ input, meta }) => (
													<>
														<input {...input} placeholder={_("search.club")} />
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</>
												)}
											</Field>
										</form>
									</>
								)}
							/>
						</div>
						<div className="filter-field">
							<label> {_("label.showcase")}</label>
							<TristateButton label={_("label.showcase")} updateFilter={updateFilter} />
						</div>
					</div>
					<div className="club-grid-container">
						{clubData ? (
							clubData.map((club) => {
								return (
									<NonPartnerClubCard
										key={club.id}
										club={club}
										countriesList={countries}
										onDeleteActionClick={onDeleteActionClick}
										onEditActionClick={onEditActionClick}
										onMigrateActionClick={onMigrateActionClick}
										onPlayerCountClick={onPlayerCountClick}
									/>
								);
							})
						) : (
							<h6 className="modal-msg">{_("info.nodata_available")}</h6>
						)}
					</div>
					<div ref={containerRef}>{hasMoreData && <Loader />}</div>
				</div>
			)}
			{showMigrate && (
				<Migrate
					club={selectedClub as unknown as ClubData}
					player={null}
					type={selectedMigrationType}
					countries={countries}
					onClose={() => {
						setShowMigrate(false);
						clubListFetchEnabledRef.current = true;
						start.current = 0;
						fetchData();
					}}
				/>
			)}

			<Modal
				show={showDeleteModal}
				title={null}
				size={"sm"}
				onClose={() => {
					setShowDeleteModal(false);
					setSelectedClub(null);
				}}
			>
				<DeleteConfirmationModal
					show={true}
					title={`${selectedClub?.name}`}
					onClose={() => {
						setShowDeleteModal(false);
						setSelectedClub(null);
					}}
					onDelete={() => {
						onDelete();
						setShowDeleteModal(false);
					}}
				/>
			</Modal>
			{showClubModal && (
				<ClubModal
					onClose={() => {
						setShowClubModal(false);
						setSelectedClub(null);
					}}
					onUpdateClub={onUpdateClub}
					club={selectedClub}
					isSubmitted={isSubmitted}
					geographicalAreaList={geographicalAreaList}
					countries={countries}
					countriesWithContinents={countriesWithContinents}
				/>
			)}
		</>
	);
};

export default NonPartnerClubManagement;
